import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../admin/App';
import { getList } from '../v4/BackendProvider';

export const usePermissions = () => {
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const { loggedIn } = useContext(AuthContext);
    const [config, setConfig] = useState({});
    const [flags, setFlags] = useState({});

    useEffect(() => {
        if (loggedIn) {
            loadPermissions();
        } else {
            setPermissions({});
            setUser(null);
            setLoading(false);
        }
    }, [loggedIn]);

    const loadPermissions = async () => {
        try {
            setLoading(true);
            const response = await getList('/me');
            setPermissions(response.data.data.permissions || {});
            setUser(response.data.data.user);
            setConfig(response.data.data.config || {});
            setFlags(response.data.data.flags || {});
        } catch (error) {
            console.error('Error loading permissions:', error);
            setPermissions({});
            setUser(null);
            setConfig({});
            setFlags({});
        } finally {
            setLoading(false);
        }
    };

    const hasPermission = (area, permission = 'read') => {
        // Special case: 'public' areas are always accessible
        if (area === 'public') {
            return true;
        }
        
        if (!permissions[area]) {
            return false;
        }
        
        if (permission === 'read') {
            return permissions[area].can_read;
        }
        
        if (permission === 'write') {
            return permissions[area].can_write;
        }
        
        return false;
    };

    const canRead = (area) => hasPermission(area, 'read');
    const canWrite = (area) => hasPermission(area, 'write');

    // Check if user is global (can access all data)
    const isGlobalUser = () => {
        return user && user.scope === 'global';
    };

    // Check if user is client-scoped
    const isClientScoped = () => {
        return user && user.scope === 'client';
    };

    // Get client ID for client-scoped users
    const getClientId = () => {
        return user ? user.client_id : null;
    };

    // Check if user has admin permissions through RBAC
    const hasAdminPermissions = () => {
        return permissions.admin?.can_write || false;
    };

    return {
        permissions,
        loading,
        user,
        config,
        flags,
        hasPermission,
        canRead,
        canWrite,
        isGlobalUser,
        isClientScoped,
        getClientId,
        hasAdminPermissions,
        refreshPermissions: loadPermissions
    };
}; 